import { useEffect, useState, useCallback } from "react";
import { adminStripeEvents } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, Zap } from "lucide-react";

const KNOWN_TYPES = [
    "checkout.session.completed",
    "customer.subscription.created",
    "customer.subscription.updated",
    "customer.subscription.deleted",
    "invoice.payment_succeeded",
    "invoice.payment_failed",
];

function fmt(ts) {
    if (!ts) return "—";
    try {
        return new Date(ts).toLocaleString();
    } catch {
        return ts;
    }
}

function fmtUnix(ts) {
    if (!ts) return "—";
    try {
        return new Date(Number(ts) * 1000).toLocaleDateString();
    } catch {
        return String(ts);
    }
}

function EventBadge({ etype }) {
    const tone = etype?.startsWith("invoice.payment_failed")
        ? "bl-badge-high"
        : etype?.endsWith(".deleted") || etype?.endsWith(".canceled")
            ? "bl-badge-medium"
            : etype?.includes("succeeded") || etype?.includes("completed")
                ? "bl-badge-low"
                : "bl-badge-neutral";
    return (
        <span className={`bl-badge ${tone} font-mono-bl text-[10px]`} title={etype}>
            {etype || "—"}
        </span>
    );
}

export default function StripeEventsAdmin() {
    const [filter, setFilter] = useState(null); // null = all
    const [data, setData] = useState({ events: [], counts: {}, total: 0 });
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = { limit: 50 };
            if (filter) params.etype = filter;
            const out = await adminStripeEvents(params);
            setData(out || { events: [], counts: {}, total: 0 });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load Stripe events");
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        load();
    }, [load]);

    const counts = data.counts || {};

    return (
        <div data-testid="stripe-events-admin">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-bl-primary" />
                    <h2 className="font-display font-semibold text-xl text-bl">
                        Stripe Webhook Events
                    </h2>
                    <span className="text-bl-muted text-sm" data-testid="stripe-events-total">
                        ({data.total || 0} total)
                    </span>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="bl-btn bl-btn-ghost text-xs flex items-center gap-1.5"
                    data-testid="stripe-events-refresh"
                    title="Refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </button>
            </div>

            <div
                className="flex items-center gap-2 flex-wrap mb-4"
                data-testid="stripe-events-filters"
            >
                <button
                    onClick={() => setFilter(null)}
                    className={`bl-badge ${filter === null ? "bl-badge-medium" : "bl-badge-neutral"} cursor-pointer`}
                    data-testid="stripe-events-filter-all"
                >
                    All · {data.total || 0}
                </button>
                {KNOWN_TYPES.map((t) => (
                    <button
                        key={t}
                        onClick={() => setFilter(t)}
                        className={`bl-badge ${filter === t ? "bl-badge-medium" : "bl-badge-neutral"} cursor-pointer font-mono-bl text-[10px]`}
                        data-testid={`stripe-events-filter-${t.replace(/\./g, "-")}`}
                    >
                        {t} · {counts[t] || 0}
                    </button>
                ))}
            </div>

            <div className="bl-card overflow-x-auto" data-testid="stripe-events-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left">
                            <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                Received
                            </th>
                            <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                Type
                            </th>
                            <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                Status
                            </th>
                            <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                Reference
                            </th>
                            <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                Period
                            </th>
                            <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                Amount
                            </th>
                            <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                Event ID
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.events.length === 0 && (
                            <tr>
                                <td
                                    colSpan={7}
                                    className="px-4 py-12 text-center text-bl-muted"
                                    data-testid="stripe-events-empty"
                                >
                                    No webhook events yet. Send a test event from your Stripe
                                    Dashboard → Developers → Webhooks to populate this view.
                                </td>
                            </tr>
                        )}
                        {data.events.map((e) => (
                            <tr
                                key={e.event_id || e.id}
                                className="border-b border-bl last:border-0"
                                data-testid={`stripe-event-row-${e.event_id || e.id}`}
                            >
                                <td className="px-4 py-3 text-xs font-mono-bl text-bl-muted whitespace-nowrap">
                                    {fmt(e.received_at)}
                                </td>
                                <td className="px-4 py-3">
                                    <EventBadge etype={e.etype} />
                                </td>
                                <td className="px-4 py-3">
                                    {e.processed ? (
                                        <span
                                            className="inline-flex items-center gap-1 text-bl-low text-xs"
                                            title="Successfully processed"
                                        >
                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                            processed
                                        </span>
                                    ) : (
                                        <span
                                            className="inline-flex items-center gap-1 text-bl-medium text-xs"
                                            title={e.error || "Pending or replayed"}
                                        >
                                            <AlertTriangle className="w-3.5 h-3.5" />
                                            {e.error ? "failed" : "pending"}
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 font-mono-bl text-[11px] text-bl break-all max-w-[200px]">
                                    {e.session_id ||
                                        e.stripe_subscription_id ||
                                        "—"}
                                </td>
                                <td className="px-4 py-3 text-xs text-bl-muted whitespace-nowrap">
                                    {e.current_period_start_ts || e.current_period_end_ts ? (
                                        <>
                                            {fmtUnix(e.current_period_start_ts)}
                                            <span className="text-bl-muted/50"> → </span>
                                            {fmtUnix(e.current_period_end_ts)}
                                        </>
                                    ) : (
                                        "—"
                                    )}
                                </td>
                                <td className="px-4 py-3 font-mono-bl text-xs text-bl tnum">
                                    {e.amount_total != null
                                        ? `$${(Number(e.amount_total) / 100).toFixed(2)}`
                                        : "—"}
                                </td>
                                <td className="px-4 py-3 font-mono-bl text-[10px] text-bl-muted break-all max-w-[180px]">
                                    {e.event_id || "—"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <p className="mt-4 text-bl-muted text-xs">
                Showing the last 50 Stripe webhook events received at{" "}
                <code className="font-mono-bl">/api/stripe/webhook</code>. Duplicate
                Stripe event ids are de-duplicated. To trigger one, run a test event
                from the Stripe Dashboard.
            </p>
        </div>
    );
}
